import type { ReactNode } from 'react'

/**
 * What she reads on the left, what she answers with on the right.
 *
 * A 16:9 television read from three metres has room for both at a size worth
 * having, and stacking them means the question scrolls away while she works —
 * on a screen where the eye travelling up and down costs more than it does at
 * arm's length.
 *
 * **Off the television this does nothing at all.** Both wrappers are
 * `display: contents` at every other scale, so their children are laid out by
 * whatever contains them exactly as they were before this component existed —
 * the phone and the tablet cannot be changed by it, rather than being changed
 * in a way somebody has to notice. The wrappers only become boxes where the
 * grid does.
 *
 * That is worth the trick: the alternative is two markup trees per game, and
 * three games' worth of "the phone looks fine, I checked".
 */
export function PlayColumns({
  reading,
  answering,
  split = 'evenly',
}: {
  /** The question, the clues, the shop — the part she thinks about. */
  readonly reading: ReactNode
  /** The keys, the pad, the box — the part she operates. */
  readonly answering: ReactNode
  /**
   * How the width is shared.
   *
   * `evenly` where both halves are things to look at. `mostly-reading` where
   * the right-hand side is only a number pad: ten keys given half a 16:9
   * screen is a wall of buttons, and the sum is what she is actually reading.
   */
  readonly split?: 'evenly' | 'mostly-reading'
}) {
  return (
    <div
      data-play-columns
      className={[
        'contents',
        '[[data-scale=tv]_&]:grid [[data-scale=tv]_&]:w-full',
        split === 'evenly'
          ? '[[data-scale=tv]_&]:grid-cols-2'
          : '[[data-scale=tv]_&]:grid-cols-[2fr_1fr]',
        '[[data-scale=tv]_&]:items-center [[data-scale=tv]_&]:gap-10',
      ].join(' ')}
    >
      <div
        className={[
          'contents',
          '[[data-scale=tv]_&]:flex [[data-scale=tv]_&]:min-w-0 [[data-scale=tv]_&]:flex-col',
          '[[data-scale=tv]_&]:items-center [[data-scale=tv]_&]:gap-6',
        ].join(' ')}
      >
        {reading}
      </div>

      <div
        className={[
          'contents',
          '[[data-scale=tv]_&]:flex [[data-scale=tv]_&]:min-w-0 [[data-scale=tv]_&]:flex-col',
          '[[data-scale=tv]_&]:items-center [[data-scale=tv]_&]:gap-6',
        ].join(' ')}
      >
        {answering}
      </div>
    </div>
  )
}
